import { useState } from 'react'
import { useTipologieBoxAttive } from '@/hooks/useBox'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Search, Package, Plus, Check } from 'lucide-react'

/**
 * Componente per selezionare tipologie box dal catalogo.
 * Mostra card con nome, prezzo, composizione e giacenza.
 * 
 * Props:
 * - selectedItems: array di { tipologia_box, quantita }
 * - onSelect: callback quando si seleziona una box
 * - onUpdateQuantity: callback per aggiornare quantità
 * - onRemove: callback per rimuovere una box
 */
export default function BoxSelector({ 
  selectedItems = [], 
  onSelect, 
  onUpdateQuantity, 
  onRemove 
}) {
  const [search, setSearch] = useState('')
  const { data: tipologieData, isLoading } = useTipologieBoxAttive()
  
  const tipologie = tipologieData?.results || tipologieData || []
  
  const filteredTipologie = tipologie.filter(t => 
    t.nome?.toLowerCase().includes(search.toLowerCase()) ||
    t.codice?.toLowerCase().includes(search.toLowerCase())
  )

  const isSelected = (id) => selectedItems.some(item => item.tipologia_box === id)
  const getSelectedItem = (id) => selectedItems.find(item => item.tipologia_box === id)

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(value || 0)
  }

  const handleSelect = (tipologia) => {
    if (isSelected(tipologia.id)) {
      onRemove?.(tipologia.id)
    } else {
      onSelect?.({
        tipologia_box: tipologia.id,
        tipologia_box_data: tipologia,
        quantita: 1,
        prezzo_unitario_cents: tipologia.prezzo_vendita_cents,
      })
    }
  }
  
  const handleUpdateQuantity = (boxId, newQuantity) => {
    const item = getSelectedItem(boxId)
    if (!item) return

    // Per le box non limitiamo la quantità alla giacenza assemblata:
    // la validazione reale avviene su componenti lato backend.
    const quantitaValidata = Math.max(1, newQuantity)
    onUpdateQuantity?.(boxId, quantitaValidata)
  }

  return (
    <div className="space-y-4">
      {/* Ricerca */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Cerca box per nome o codice..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Riepilogo selezionati */}
      {selectedItems.length > 0 && (
        <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
          <p className="text-sm font-medium mb-2">
            Box selezionate: {selectedItems.length}
          </p>
          <div className="readable-rows overflow-hidden rounded-md border border-border">
            {selectedItems.map((item) => (
              <div key={item.tipologia_box} className="readable-row flex flex-col gap-2 px-3 py-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                <span className="min-w-0 flex-1 font-medium">{item.tipologia_box_data?.nome || `Box #${item.tipologia_box}`}</span>
                <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 w-6 p-0"
                    onClick={() => handleUpdateQuantity(item.tipologia_box, item.quantita - 1)}
                    disabled={item.quantita <= 1}
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    aria-label={`Quantità di ${item.tipologia_box_data?.nome || `Box #${item.tipologia_box}`}`}
                    min="1"
                    value={item.quantita}
                    onChange={(e) => handleUpdateQuantity(item.tipologia_box, parseInt(e.target.value) || 1)}
                    className="w-16 h-6 text-center p-0"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 w-6 p-0"
                    onClick={() => handleUpdateQuantity(item.tipologia_box, item.quantita + 1)}
                  >
                    +
                  </Button>
                  <span className="ml-2 w-24 text-right tabular-nums text-muted-foreground">
                    {formatCurrency((item.prezzo_unitario_cents / 100) * item.quantita)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lista box */}
      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">
          Caricamento box...
        </div>
      ) : filteredTipologie.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>{search ? 'Nessuna box trovata' : 'Nessuna box disponibile'}</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 max-h-96 overflow-y-auto">
          {filteredTipologie.map((tipologia) => {
            const selected = isSelected(tipologia.id)
            const giacenza = tipologia.giacenza_disponibile ?? 0
            
            return (
              <div
                key={tipologia.id}
                onClick={() => handleSelect(tipologia)}
                className={`
                  p-4 border rounded-lg transition-all
                  ${selected 
                    ? 'border-primary bg-primary/5 ring-2 ring-primary cursor-pointer' 
                    : 'hover:bg-accent hover:border-accent-foreground/20 cursor-pointer'
                  }
                `}
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium">{tipologia.nome}</p>
                    <p className="text-xs text-muted-foreground font-mono">{tipologia.codice}</p>
                  </div>
                  {selected && (
                    <div className="bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="h-3 w-3" />
                    </div>
                  )}
                </div>
                
                <div className="flex justify-between items-end">
                  <div className="space-y-1">
                    <div className="flex gap-1 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {tipologia.num_prodotti || 0} prodotti
                      </Badge>
                      {giacenza <= 0 && (
                        <Badge variant="destructive" className="text-xs">
                          Giacenza {giacenza}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Giacenza: <span className={giacenza > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {giacenza}
                      </span>
                    </p>
                  </div>
                  <p className="font-bold text-lg">
                    {formatCurrency(tipologia.prezzo_vendita_cents / 100)}
                  </p>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
